import type { Dict } from "../types";

export const dashboardShell: Dict = {
  en: {

    // Owner toast messages (errorToast / successToast wrapper)
    "Owner.toast.loadSalonFailed": "Could not load salon information",
    "Owner.toast.saveSalonInfoFailed": "Could not save salon information",
    "Owner.toast.salonInfoSaved": "Salon information saved",
    "Owner.toast.saveSettingsFailed": "Could not save salon settings",
    "Owner.toast.settingsSaved": "Salon settings updated",
    "Owner.toast.savePoliciesFailed": "Could not save policies",
    "Owner.toast.policiesSaved": "Policies updated",
    "Owner.toast.invalidLogoFile": "Invalid logo file",
    "Owner.toast.logoFileTooLarge": "Logo file is too large",
    "Owner.toast.uploadLogoFailed": "Could not upload logo",
    "Owner.toast.removeLogoFailed": "Could not remove logo",
    "Owner.toast.oldLogoNotRemoved": "New logo saved, but the previous file could not be deleted",
    "Owner.toast.oldHairdresserPhotoNotRemoved": "New photo saved, but the previous file could not be deleted",
    "Owner.toast.logoUpdated": "Logo updated",
    "Owner.toast.logoRemoved": "Logo removed",
    "Owner.toast.copyLinkFailed": "Could not copy the link",
    "Owner.toast.linkCopied": "Link copied to clipboard",
    "Owner.toast.qrDownloadFailed": "Could not download QR code",
    "Owner.toast.qrDownloaded": "QR code downloaded",
    "Owner.toast.updateOpeningHoursFailed": "Could not update opening hours",
    "Owner.toast.openingHoursUpdated": "Opening hours updated",
    "Owner.toast.setSalonHours": "Set the salon hours first",
    "Owner.toast.setHairdresserHours": "Set hours for this employee",
    "Owner.toast.hairdresserHoursOutOfRange": "Employee hours must fit within salon hours",
    "Owner.toast.deleteClosureFailed": "Could not delete closure",
    "Owner.toast.deleteOpeningFailed": "Could not delete opening",
    "Owner.toast.closureDeleted": "Closure deleted",
    "Owner.toast.openingDeleted": "Opening deleted",
    "Owner.toast.loadRoomsFailed": "Could not load rooms",
    "Owner.toast.saveRoomFailed": "Could not save room",
    "Owner.toast.roomCreatedLinksPartial": "Room created, but some equipment could not be linked. Open the room to finish.",
    "Owner.toast.deleteRoomFailed": "Could not delete room",
    "Owner.toast.saveEquipmentFailed": "Could not save equipment",
    "Owner.toast.deleteEquipmentFailed": "Could not delete equipment",
    "Owner.toast.roomSaved": "Room saved",
    "Owner.toast.roomDeleted": "Room deleted",
    "Owner.toast.equipmentSaved": "Equipment saved",
    "Owner.toast.equipmentDeleted": "Equipment deleted",
    "Owner.toast.saveServiceFailed": "Could not save service",
    "Owner.toast.deleteServiceFailed": "Could not delete service",
    "Owner.toast.serviceSaved": "Service saved",
    "Owner.toast.serviceDeleted": "Service deleted",
    "Owner.toast.saveSectionFailed": "Could not save section",
    "Owner.toast.deleteSectionFailed": "Could not delete section",
    "Owner.toast.renameSectionFailed": "Could not rename section",
    "Owner.toast.sectionCreated": "Section created",
    "Owner.toast.scopeAssignmentPartial": "Assigned to {assigned} of {total} active employees",
    "Owner.toast.scopeAssignmentIncomplete": "Could not finish assigning every employee. Please retry.",
    "Owner.toast.sectionRenamed": "Section renamed",
    "Owner.toast.fillRequiredFields": "Please fill in the required fields",
    "Owner.toast.enterDuration": "Please enter a duration",
    "Owner.toast.enterSectionName": "Please enter a section name",
    "Owner.toast.noSalonFound": "No salon found",
    "Owner.toast.invalidImageFile": "Invalid image file",
    "Owner.toast.maxPortfolioImages": "Maximum portfolio images reached",
    "Owner.toast.selectOnlyImages": "Please select images only",
    "Owner.toast.hairdresserNameRequired": "Employee name is required",
    "Owner.toast.aboutMeLimit": "About me must be 200 characters or less",
    "Owner.toast.saveHairdresserFailed": "Could not save employee",
    "Owner.toast.deleteHairdresserFailed": "Could not delete employee",
    "Owner.toast.hairdresserSaved": "Employee saved",
    "Owner.toast.hairdresserDeleted": "Employee deleted",
    "Owner.toast.bufferUpdateFailed": "Could not update buffer time",
    "Owner.toast.bufferUpdated": "Buffer time updated",
    "Owner.toast.loadBufferFailed": "Could not load employees",
    "Owner.toast.loadAnalyticsFailed": "Could not load QR analytics",
    "Owner.toast.subscriptionPortalFailed": "Could not open the customer portal",
    "Owner.toast.locationUpdateFailed": "Could not update location",
    "Owner.toast.locationNameRequired": "Location name is required",
    "Owner.toast.locationAdded": "Location added",
    "Owner.toast.locationArchived": "Location archived",
    "Owner.toast.locationRestored": "Location restored",
    "Owner.toast.selectedLocationUnavailable": "The selected location is no longer available. Back to the main salon.",
    "Owner.toast.specializationsUpdateFailed": "Could not update specializations",
    "Owner.toast.specializationsSaved": "Specializations saved",
    "Owner.toast.loadEmployeeRatesFailed": "Failed to load this employee's prices. Please retry.",
    "Owner.toast.noSalonLinked": "No salon linked to your account",
    "Owner.toast.loadInvoiceFailed": "Could not load invoice",
    "Owner.toast.cancelAppointmentFailed": "Could not cancel appointment",
    "Owner.toast.removeAppointmentFailed": "Could not remove the appointment. Please try again.",
    "Owner.toast.appointmentCancelled": "Appointment cancelled",
    "Owner.toast.mustApprovePending": "You must approve a pending appointment first",
    "Owner.toast.cancelServiceFailed": "Could not cancel service",
    "Owner.toast.deleteServiceFromAppointmentFailed": "Could not delete service",
    "Owner.toast.serviceCancelledShort": "Service cancelled",
    "Owner.toast.serviceDeletedShort": "Service deleted",
    "Owner.toast.invoiceWarning": "This appointment has an invoice. Update it after the change.",
    "Owner.toast.cancelServiceFirst": "You must cancel the service before deleting it",
    "Owner.toast.markAttendanceFailed": "Could not update attendance",
    "Owner.toast.markedPresent": "Marked as present",
    "Owner.toast.markedNoshow": "Marked as no-show",
    "Owner.toast.reorderColumnsFailed": "Could not save column order. Restoring previous order.",
    "Owner.toast.saveSubsectionFailed": "Could not save subsection",
    "Owner.toast.deleteSubsectionFailed": "Could not delete subsection",
    "Owner.toast.subsectionAdded": "Subsection added",
    "Owner.toast.subsectionUpdated": "Subsection updated",
    "Owner.toast.subsectionDeleted": "Subsection deleted",

    // Owner Dashboard - Appointment Management
    "Owner.manageAppointment": "Manage Appointment",
    "Owner.whatToDo": "What would you like to do with this appointment?",
    "Owner.smsNotDeliveredTitle": "Reminder not delivered",
    "Owner.smsNotDeliveredHint": "This client did not receive their SMS reminder. Consider reaching them another way.",
    "Owner.cancelledAppointmentOptions": "This appointment has been cancelled. You can delete it from your calendar.",

    "Owner.confirmCancellation": "Confirm cancellation",
    "Owner.currentAppointment": "Current Appointment:",
    "Owner.bookedByOnlineClient": "Booked online by client",
    "Owner.bookedByVoiceAI": "Booked by voice AI over the phone",
    "Owner.bookedByStaff": "Booked by {name} on the calendar",
    "Owner.appointmentIsCancelled": "This appointment has been cancelled",
    "Owner.cancelAppointment": "Cancel",
    "Owner.prebookAppointment": "Pre-book",
    "Owner.removeFromCalendarAction": "Remove from calendar",
    "Owner.confirmRemoveFromCalendar": "Remove from calendar",
    "Owner.confirmRemoveQuestion": "Remove this appointment from the calendar?",
    "Owner.removeKeepsDataNote": "It stays in the cancellation log, reports and client history. Nothing is deleted.",
    "Owner.removeFromCalendarConfirm": "Remove",
    "Owner.editServices": "Edit",
    "Owner.editServicesDesc": "Add, remove, or reorder services",
    "Owner.notesOptional": "Notes (Optional)",
    "Owner.confirmCancelQuestion": "Are you sure you want to cancel this appointment?",
    "Owner.customerRebook": "The customer will need to book a new appointment if they wish to reschedule.",
    "Owner.appointmentDetails": "Appointment Details:",
    "Owner.contact": "Contact:",
    "Owner.close": "Close",
    "Owner.back": "Back",
    "Owner.confirmCancellationButton": "Confirm Cancellation",
    "Owner.confirmDeleteButton": "Delete Permanently",
    "Owner.delete": "Delete",
    "Owner.confirmCancel": "Confirm Cancellation",
    "Owner.attendanceTracking": "Attendance Tracking",
    "Owner.present": "Present",
    "Owner.noshow": "No-show",
    "Owner.markedPresent": "Marked as present",
    "Owner.markedNoshow": "Marked as no-show",
    "Owner.unmarkedPresent": "Attendance removed",
    "Owner.unmarkedNoshow": "No-show removed",
    "Owner.viewInvoice": "View Invoice",
    "Owner.viewCustomerFile": "View customer file",
    "Owner.specificChoice": "Specific Choice",
    "Owner.autoAssignment": "Auto Assignment",

    // OwnerDashboard
    "OwnerDashboard.salonManagement": "Salon Management",
    "OwnerDashboard.manageAppointments": "Manage all your salon appointments",
    "OwnerDashboard.signOut": "Sign Out",
    "OwnerDashboard.listView": "Salon Management",
    "OwnerDashboard.calendarView": "Calendar & Analytics View",
    "OwnerDashboard.calendar": "Calendar",
    "OwnerDashboard.customers": "Customers",
    "OwnerDashboard.proFeature": "Upgrade to Pro to unlock this feature",
    "OwnerDashboard.enterpriseFeature": "Upgrade to Enterprise to unlock this feature",
    "OwnerDashboard.planLocked.manageSubscription": "Manage subscription",
    "OwnerDashboard.noSalonLinked": "No Salon Linked",
    "OwnerDashboard.noSalonLinkedDesc": "Your account is not linked to a salon. Please contact support.",
    "OwnerDashboard.noAccessConfigured": "No access is configured for your account. Please contact the salon owner.",
    "OwnerDashboard.error": "Error",
    "OwnerDashboard.failedToLoadSalon": "Failed to load salon information",
    "OwnerDashboard.failedToLoadAppointments": "Failed to load appointments",
    "OwnerDashboard.success": "Success!",
    "OwnerDashboard.appointmentCancelled": "Appointment cancelled successfully",
    "OwnerDashboard.appointmentRemovedFromCalendar": "Appointment removed from the calendar",
    "OwnerDashboard.failedToRemoveAppointment": "Failed to remove the appointment",
    "OwnerDashboard.appointmentApproved": "Appointment approved successfully",
    "OwnerDashboard.appointmentChangedMeanwhile": "This appointment changed in the meantime (it was cancelled or already handled). The list has been refreshed.",
    "AppointmentRemoteChange.moved": "This appointment was just moved on another device.",
    "AppointmentRemoteChange.cancelled": "This appointment was just cancelled on another device.",
    "AppointmentRemoteChange.modified": "This appointment was just modified on another device.",
    "AppointmentRemoteChange.notInView": "This appointment is no longer in the calendar shown.",
    "AppointmentRemoteChange.reloadFailed": "This appointment could not be reloaded. Please try again.",
    "OwnerDashboard.appointmentRejected": "Appointment rejected successfully",
    "OwnerDashboard.failedToApprove": "Failed to approve appointment",
    "OwnerDashboard.failedToReject": "Failed to reject appointment",
    "OwnerDashboard.failedToCancelAppointment": "Failed to cancel appointment",
    "OwnerDashboard.appointmentRescheduled": "Appointment rescheduled successfully",
    "OwnerDashboard.failedToReschedule": "Failed to reschedule appointment",
    "OwnerDashboard.appointmentStatusUpdated": "Appointment status updated",
    "OwnerDashboard.failedToUpdateStatus": "Failed to update status",
    "OwnerDashboard.pendingApprovals": "Pending Approvals",
    "OwnerDashboard.pendingApproval": "Pending Approval",
    "OwnerDashboard.mustApprovePending": "Please approve or reject this appointment before making changes",
    "OwnerDashboard.pending": "Pending",
    "OwnerDashboard.approve": "Approve",
    "OwnerDashboard.reject": "Reject",
    "OwnerDashboard.cancel": "Cancel",
    "OwnerDashboard.delete": "Delete",
    "OwnerDashboard.warning": "Warning",
    "OwnerDashboard.emailNotSent": "Failed to send confirmation email",
    "OwnerDashboard.showingItems": "Showing {start} to {end} of {total}",
    "OwnerDashboard.pageOf": "Page {current} of {total}",
    "OwnerDashboard.salonInfo": "Salon Information",
    "OwnerDashboard.openingHours": "Opening Hours",
    "OwnerDashboard.services": "Services",
    "OwnerDashboard.roomsEquipment": "Rooms & Equipment",
    "OwnerDashboard.hairdressers": "Employees",
    "OwnerDashboard.employees": "Employees",
    "OwnerDashboard.qrAnalytics": "QR Analytics",
    "OwnerDashboard.allCustomers": "All Customers",
    "OwnerDashboard.allServices": "All Services",
    "OwnerDashboard.allHairdressers": "All Employees",
    "OwnerDashboard.noPendingAppointments": "No pending appointments",
    "OwnerDashboard.noResultsFound": "No results found",
    "OwnerDashboard.filterByCustomer": "Filter by customer",
    "OwnerDashboard.filterByService": "Filter by service",
    "OwnerDashboard.filterByHairdresser": "Filter by employee",
    "OwnerDashboard.bookAppointment": "Book Appointment",
    "OwnerDashboard.selectClientServiceDatetime": "Select client, service, employee, date and time",
    "OwnerDashboard.menu": "Menu",
    "OwnerDashboard.today": "Today",
    "OwnerDashboard.daily": "Daily",
    "OwnerDashboard.weekly": "Weekly",
    "OwnerDashboard.monthly": "Monthly",
    "OwnerDashboard.activityLog": "Activity log",
    "OwnerDashboard.permissions": "Permissions",
    "OwnerDashboard.marketing": "Marketing",
    "OwnerDashboard.ia": "AI Configuration",
    "OwnerDashboard.approval": "Approval",
    "OwnerDashboard.add": "Add",
    "OwnerDashboard.sidebarScrollMore": "See more modules",
    "OwnerDashboard.toggleModules": "Show modules",

    // Notifications
    "notifications.title": "Notifications",
    "notifications.pendingApprovals": "Pending approvals",
    "notifications.cancelledAppointments": "Cancelled appointments",
    "notifications.recentCancellations": "Recent cancellations:",
    "notifications.toApprove": "Appointments to approve:",
  },
  fr: {

    // Owner toast messages (errorToast / successToast wrapper)
    "Owner.toast.loadSalonFailed": "Impossible de charger les informations du salon",
    "Owner.toast.saveSalonInfoFailed": "Impossible d'enregistrer les informations du salon",
    "Owner.toast.salonInfoSaved": "Informations du salon enregistrées",
    "Owner.toast.saveSettingsFailed": "Impossible d'enregistrer les paramètres du salon",
    "Owner.toast.settingsSaved": "Paramètres du salon mis à jour",
    "Owner.toast.savePoliciesFailed": "Impossible d'enregistrer les politiques",
    "Owner.toast.policiesSaved": "Politiques mises à jour",
    "Owner.toast.invalidLogoFile": "Fichier de logo invalide",
    "Owner.toast.logoFileTooLarge": "Le fichier du logo est trop volumineux",
    "Owner.toast.uploadLogoFailed": "Impossible de téléverser le logo",
    "Owner.toast.removeLogoFailed": "Impossible de supprimer le logo",
    "Owner.toast.oldLogoNotRemoved": "Nouveau logo enregistré, mais l'ancien fichier n'a pas pu être supprimé",
    "Owner.toast.oldHairdresserPhotoNotRemoved": "Nouvelle photo enregistrée, mais l'ancien fichier n'a pas pu être supprimé",
    "Owner.toast.logoUpdated": "Logo mis à jour",
    "Owner.toast.logoRemoved": "Logo supprimé",
    "Owner.toast.copyLinkFailed": "Impossible de copier le lien",
    "Owner.toast.linkCopied": "Lien copié dans le presse-papiers",
    "Owner.toast.qrDownloadFailed": "Impossible de télécharger le code QR",
    "Owner.toast.qrDownloaded": "Code QR téléchargé",
    "Owner.toast.updateOpeningHoursFailed": "Impossible de mettre à jour les heures d'ouverture",
    "Owner.toast.openingHoursUpdated": "Heures d'ouverture mises à jour",
    "Owner.toast.setSalonHours": "Définissez d'abord les heures du salon",
    "Owner.toast.setHairdresserHours": "Définissez les heures de cet employé",
    "Owner.toast.hairdresserHoursOutOfRange": "Les heures de l'employé doivent être incluses dans celles du salon",
    "Owner.toast.deleteClosureFailed": "Impossible de supprimer la fermeture",
    "Owner.toast.deleteOpeningFailed": "Impossible de supprimer l'ouverture",
    "Owner.toast.closureDeleted": "Fermeture supprimée",
    "Owner.toast.openingDeleted": "Ouverture supprimée",
    "Owner.toast.loadRoomsFailed": "Impossible de charger les salles",
    "Owner.toast.saveRoomFailed": "Impossible d'enregistrer la salle",
    "Owner.toast.roomCreatedLinksPartial": "Salle créée, mais certains équipements n'ont pas pu être rattachés. Rouvrez la salle pour terminer.",
    "Owner.toast.deleteRoomFailed": "Impossible de supprimer la salle",
    "Owner.toast.saveEquipmentFailed": "Impossible d'enregistrer l'équipement",
    "Owner.toast.deleteEquipmentFailed": "Impossible de supprimer l'équipement",
    "Owner.toast.roomSaved": "Salle enregistrée",
    "Owner.toast.roomDeleted": "Salle supprimée",
    "Owner.toast.equipmentSaved": "Équipement enregistré",
    "Owner.toast.equipmentDeleted": "Équipement supprimé",
    "Owner.toast.saveServiceFailed": "Impossible d'enregistrer le service",
    "Owner.toast.deleteServiceFailed": "Impossible de supprimer le service",
    "Owner.toast.serviceSaved": "Service enregistré",
    "Owner.toast.serviceDeleted": "Service supprimé",
    "Owner.toast.saveSectionFailed": "Impossible d'enregistrer la section",
    "Owner.toast.deleteSectionFailed": "Impossible de supprimer la section",
    "Owner.toast.renameSectionFailed": "Impossible de renommer la section",
    "Owner.toast.sectionCreated": "Section créée",
    "Owner.toast.scopeAssignmentPartial": "Attribué à {assigned} des {total} employés actifs",
    "Owner.toast.scopeAssignmentIncomplete": "Impossible de terminer l'attribution à tous les employés. Réessayez.",
    "Owner.toast.sectionRenamed": "Section renommée",
    "Owner.toast.fillRequiredFields": "Veuillez remplir les champs requis",
    "Owner.toast.enterDuration": "Veuillez entrer une durée",
    "Owner.toast.enterSectionName": "Veuillez entrer un nom de section",
    "Owner.toast.noSalonFound": "Aucun salon trouvé",
    "Owner.toast.invalidImageFile": "Fichier image invalide",
    "Owner.toast.maxPortfolioImages": "Nombre maximal d'images du portfolio atteint",
    "Owner.toast.selectOnlyImages": "Veuillez sélectionner uniquement des images",
    "Owner.toast.hairdresserNameRequired": "Le nom de l'employé est requis",
    "Owner.toast.aboutMeLimit": "La présentation doit faire 200 caractères ou moins",
    "Owner.toast.saveHairdresserFailed": "Impossible d'enregistrer l'employé",
    "Owner.toast.deleteHairdresserFailed": "Impossible de supprimer l'employé",
    "Owner.toast.hairdresserSaved": "Employé enregistré",
    "Owner.toast.hairdresserDeleted": "Employé supprimé",
    "Owner.toast.bufferUpdateFailed": "Impossible de mettre à jour le temps tampon",
    "Owner.toast.bufferUpdated": "Temps tampon mis à jour",
    "Owner.toast.loadBufferFailed": "Impossible de charger les employés",
    "Owner.toast.loadAnalyticsFailed": "Impossible de charger les statistiques QR",
    "Owner.toast.subscriptionPortalFailed": "Impossible d'ouvrir le portail client",
    "Owner.toast.locationUpdateFailed": "Impossible de mettre à jour la succursale",
    "Owner.toast.locationNameRequired": "Le nom de la succursale est requis",
    "Owner.toast.locationAdded": "Succursale ajoutée",
    "Owner.toast.locationArchived": "Succursale archivée",
    "Owner.toast.locationRestored": "Succursale restaurée",
    "Owner.toast.selectedLocationUnavailable": "L'emplacement sélectionné n'est plus disponible. Retour au salon principal.",
    "Owner.toast.specializationsUpdateFailed": "Impossible de mettre à jour les spécialisations",
    "Owner.toast.specializationsSaved": "Spécialisations enregistrées",
    "Owner.toast.loadEmployeeRatesFailed": "Impossible de charger les prix de cet employé. Réessayez.",
    "Owner.toast.noSalonLinked": "Aucun salon lié à votre compte",
    "Owner.toast.loadInvoiceFailed": "Impossible de charger la facture",
    "Owner.toast.cancelAppointmentFailed": "Impossible d'annuler le rendez-vous",
    "Owner.toast.removeAppointmentFailed": "Impossible de retirer le rendez-vous. Réessayez.",
    "Owner.toast.appointmentCancelled": "Rendez-vous annulé",
    "Owner.toast.mustApprovePending": "Vous devez d'abord approuver un rendez-vous en attente",
    "Owner.toast.cancelServiceFailed": "Impossible d'annuler le service",
    "Owner.toast.deleteServiceFromAppointmentFailed": "Impossible de supprimer le service",
    "Owner.toast.serviceCancelledShort": "Service annulé",
    "Owner.toast.serviceDeletedShort": "Service supprimé",
    "Owner.toast.invoiceWarning": "Ce rendez-vous a une facture. Pensez à l'ajuster après la modification.",
    "Owner.toast.cancelServiceFirst": "Vous devez annuler le service avant de le supprimer",
    "Owner.toast.markAttendanceFailed": "Impossible de mettre à jour la présence",
    "Owner.toast.markedPresent": "Marqué présent",
    "Owner.toast.markedNoshow": "Marqué absent",
    "Owner.toast.reorderColumnsFailed": "Impossible d'enregistrer l'ordre des colonnes. Ordre précédent restauré.",
    "Owner.toast.saveSubsectionFailed": "Impossible d'enregistrer la sous-section",
    "Owner.toast.deleteSubsectionFailed": "Impossible de supprimer la sous-section",
    "Owner.toast.subsectionAdded": "Sous-section ajoutée",
    "Owner.toast.subsectionUpdated": "Sous-section mise à jour",
    "Owner.toast.subsectionDeleted": "Sous-section supprimée",

    // Owner Dashboard - Appointment Management
    "Owner.manageAppointment": "Gérer le rendez-vous",
    "Owner.whatToDo": "Que souhaitez-vous faire avec ce rendez-vous ?",
    "Owner.smsNotDeliveredTitle": "Rappel non délivré",
    "Owner.smsNotDeliveredHint": "Ce client n'a pas reçu son rappel par SMS. Pensez à le contacter autrement.",
    "Owner.cancelledAppointmentOptions": "Ce rendez-vous a été annulé. Vous pouvez le supprimer de votre calendrier.",

    "Owner.confirmCancellation": "Confirmer l'annulation",
    "Owner.currentAppointment": "Rendez-vous actuel :",
    "Owner.bookedByOnlineClient": "Pris par client en ligne",
    "Owner.bookedByVoiceAI": "Pris par l'IA vocale au téléphone",
    "Owner.bookedByStaff": "Pris par {name} sur le calendrier",
    "Owner.appointmentIsCancelled": "Ce rendez-vous a été annulé",
    "Owner.cancelAppointment": "Annuler",
    "Owner.prebookAppointment": "Pré-réserver",
    "Owner.removeFromCalendarAction": "Retirer du calendrier",
    "Owner.confirmRemoveFromCalendar": "Retirer du calendrier",
    "Owner.confirmRemoveQuestion": "Retirer ce rendez-vous du calendrier ?",
    "Owner.removeKeepsDataNote": "Il reste dans le Journal des annulations, les rapports et l'historique client. Rien n'est supprimé.",
    "Owner.removeFromCalendarConfirm": "Retirer",
    "Owner.editServices": "Modifier",
    "Owner.editServicesDesc": "Ajouter, retirer ou réordonner les services",
    "Owner.notesOptional": "Notes (optionnel)",
    "Owner.confirmCancelQuestion": "Êtes-vous sûr de vouloir annuler ce rendez-vous ?",
    "Owner.customerRebook": "Le client devra prendre un nouveau rendez-vous s'il souhaite reporter.",
    "Owner.appointmentDetails": "Détails du rendez-vous :",
    "Owner.contact": "Contact :",
    "Owner.close": "Fermer",
    "Owner.back": "Retour",
    "Owner.confirmCancellationButton": "Confirmer l'annulation",
    "Owner.confirmDeleteButton": "Supprimer définitivement",
    "Owner.delete": "Supprimer",
    "Owner.confirmCancel": "Confirmer l'annulation",
    "Owner.attendanceTracking": "Suivi de présence",
    "Owner.present": "Présent",
    "Owner.noshow": "No-show",
    "Owner.markedPresent": "Marqué comme présent",
    "Owner.markedNoshow": "Marqué comme no-show",
    "Owner.unmarkedPresent": "Présence retirée",
    "Owner.unmarkedNoshow": "No-show retiré",
    "Owner.viewInvoice": "Voir facture",
    "Owner.viewCustomerFile": "Voir le dossier client",
    "Owner.specificChoice": "Choix spécifique",
    "Owner.autoAssignment": "Assignation automatique",

    // OwnerDashboard
    "OwnerDashboard.salonManagement": "Gestion du salon",
    "OwnerDashboard.manageAppointments": "Gérez tous les rendez-vous de votre salon",
    "OwnerDashboard.signOut": "Se déconnecter",
    "OwnerDashboard.listView": "Information à propos du salon",
    "OwnerDashboard.calendarView": "Calendrier & Analyses",
    "OwnerDashboard.calendar": "Calendrier",
    "OwnerDashboard.customers": "Clients",
    "OwnerDashboard.proFeature": "Passez au plan Pro pour débloquer cette fonctionnalité",
    "OwnerDashboard.enterpriseFeature": "Passez au plan Entreprise pour débloquer cette fonctionnalité",
    "OwnerDashboard.planLocked.manageSubscription": "Gérer l'abonnement",
    "OwnerDashboard.noSalonLinked": "Aucun salon associé",
    "OwnerDashboard.noSalonLinkedDesc": "Votre compte n'est pas lié à un salon. Veuillez contacter le support.",
    "OwnerDashboard.noAccessConfigured": "Aucun accès n'est configuré pour votre compte. Contactez le propriétaire du salon.",
    "OwnerDashboard.error": "Erreur",
    "OwnerDashboard.failedToLoadSalon": "Échec du chargement des informations du salon",
    "OwnerDashboard.failedToLoadAppointments": "Échec du chargement des rendez-vous",
    "OwnerDashboard.success": "Succès!",
    "OwnerDashboard.appointmentCancelled": "Rendez-vous annulé avec succès",
    "OwnerDashboard.appointmentRemovedFromCalendar": "Rendez-vous retiré du calendrier",
    "OwnerDashboard.failedToRemoveAppointment": "Échec du retrait du rendez-vous",
    "OwnerDashboard.appointmentApproved": "Rendez-vous approuvé avec succès",
    "OwnerDashboard.appointmentChangedMeanwhile": "Ce rendez-vous a changé entre temps (il a été annulé ou déjà traité). La liste a été mise à jour.",
    "AppointmentRemoteChange.moved": "Ce rendez-vous vient d'être déplacé sur un autre appareil.",
    "AppointmentRemoteChange.cancelled": "Ce rendez-vous vient d'être annulé sur un autre appareil.",
    "AppointmentRemoteChange.modified": "Ce rendez-vous vient d'être modifié sur un autre appareil.",
    "AppointmentRemoteChange.notInView": "Ce rendez-vous n'est plus dans le calendrier affiché.",
    "AppointmentRemoteChange.reloadFailed": "Ce rendez-vous n'a pas pu être rechargé. Veuillez réessayer.",
    "OwnerDashboard.appointmentRejected": "Rendez-vous rejeté avec succès",
    "OwnerDashboard.failedToApprove": "Échec de l'approbation du rendez-vous",
    "OwnerDashboard.failedToReject": "Échec du rejet du rendez-vous",
    "OwnerDashboard.failedToCancelAppointment": "Échec de l'annulation du rendez-vous",
    "OwnerDashboard.appointmentRescheduled": "Rendez-vous reprogrammé avec succès",
    "OwnerDashboard.failedToReschedule": "Échec de la reprogrammation du rendez-vous",
    "OwnerDashboard.appointmentStatusUpdated": "Statut du rendez-vous mis à jour",
    "OwnerDashboard.failedToUpdateStatus": "Échec de la mise à jour du statut",
    "OwnerDashboard.pendingApprovals": "Approbations en attente",
    "OwnerDashboard.pendingApproval": "Approbation en attente",
    "OwnerDashboard.mustApprovePending":
      "Veuillez approuver ou rejeter ce rendez-vous avant d'effectuer des modifications",
    "OwnerDashboard.pending": "En attente",
    "OwnerDashboard.approve": "Approuver",
    "OwnerDashboard.reject": "Rejeter",
    "OwnerDashboard.cancel": "Annuler",
    "OwnerDashboard.delete": "Supprimer",
    "OwnerDashboard.warning": "Avertissement",
    "OwnerDashboard.emailNotSent": "Impossible d'envoyer le courriel de confirmation",
    "OwnerDashboard.showingItems": "Affichage de {start} à {end} sur {total}",
    "OwnerDashboard.pageOf": "Page {current} sur {total}",
    "OwnerDashboard.salonInfo": "Informations du salon",
    "OwnerDashboard.openingHours": "Heures d'ouverture",
    "OwnerDashboard.services": "Services",
    "OwnerDashboard.roomsEquipment": "Salles et équipements",
    "OwnerDashboard.hairdressers": "Employés",
    "OwnerDashboard.employees": "Employés",
    "OwnerDashboard.qrAnalytics": "Analytiques QR",
    "OwnerDashboard.allCustomers": "Tous les clients",
    "OwnerDashboard.allServices": "Tous les services",
    "OwnerDashboard.allHairdressers": "Tous les employés",
    "OwnerDashboard.noPendingAppointments": "Aucun rendez-vous en attente",
    "OwnerDashboard.noResultsFound": "Aucun résultat trouvé",
    "OwnerDashboard.filterByCustomer": "Filtrer par client",
    "OwnerDashboard.filterByService": "Filtrer par service",
    "OwnerDashboard.filterByHairdresser": "Filtrer par employé",
    "OwnerDashboard.bookAppointment": "Réserver un rendez-vous",
    "OwnerDashboard.selectClientServiceDatetime": "Sélectionner le client, le service, l'employé, la date et l'heure",
    "OwnerDashboard.menu": "Menu",
    "OwnerDashboard.today": "Aujourd'hui",
    "OwnerDashboard.daily": "Jour",
    "OwnerDashboard.weekly": "Semaine",
    "OwnerDashboard.monthly": "Mois",
    "OwnerDashboard.activityLog": "Journal d'activité",
    "OwnerDashboard.permissions": "Permissions",
    "OwnerDashboard.marketing": "Marketing",
    "OwnerDashboard.ia": "Configuration IA",
    "OwnerDashboard.approval": "Approbation",
    "OwnerDashboard.add": "Ajouter",
    "OwnerDashboard.sidebarScrollMore": "Voir plus de modules",
    "OwnerDashboard.toggleModules": "Afficher les modules",

    // Notifications
    "notifications.title": "Notifications",
    "notifications.pendingApprovals": "Approbations en attente",
    "notifications.cancelledAppointments": "Rendez-vous annulés",
    "notifications.recentCancellations": "Annulations récentes :",
    "notifications.toApprove": "Rendez-vous à approuver :",
  },
};
